/**
 * SystemOversight — the autonomous-system oversight panel for the Reports page.
 *
 * Answers one question at a glance: *is the autonomous system improving?* It
 * reads /api/reporting/system-trajectory (verdict + drivers + rolling
 * trajectory + forward P&L forecast + top winning trades + a plain-language
 * narrative) and degrades gracefully to a "warming up" state when not enough
 * trades have closed yet (early paper testing).
 */
import React, { useEffect, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Trophy,
  Telescope,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { reportingApi } from '../../lib/api';
import type { SystemTrajectory } from '../../types/titan';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import TrajectoryChart from './TrajectoryChart';

const fmtMoney = (v: number, dp = 0) =>
  `${v >= 0 ? '+' : '-'}$${Math.abs(v).toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;

const pnlClass = (v: number) =>
  v > 0 ? 'text-emerald-500' : v < 0 ? 'text-rose-500' : 'text-muted-foreground';

const VERDICT = {
  improving: {
    label: 'Improving',
    Icon: TrendingUp,
    ring: 'border-emerald-500/40',
    glow: 'from-emerald-500/15 to-transparent',
    text: 'text-emerald-500',
    chip: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  },
  flat: {
    label: 'Holding Steady',
    Icon: Minus,
    ring: 'border-amber-500/40',
    glow: 'from-amber-500/15 to-transparent',
    text: 'text-amber-500',
    chip: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  },
  degrading: {
    label: 'Degrading',
    Icon: TrendingDown,
    ring: 'border-rose-500/40',
    glow: 'from-rose-500/15 to-transparent',
    text: 'text-rose-500',
    chip: 'bg-rose-500/15 text-rose-500 border-rose-500/30',
  },
  insufficient_data: {
    label: 'Warming Up',
    Icon: Activity,
    ring: 'border-border',
    glow: 'from-primary/10 to-transparent',
    text: 'text-muted-foreground',
    chip: 'bg-muted text-muted-foreground border-border',
  },
} as const;

function KPI({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="rounded-lg border bg-card/50 p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-lg font-bold ${valueClass || ''}`}>{value}</p>
    </div>
  );
}

export default function SystemOversight({ periodDays }: { periodDays: number }) {
  const [data, setData] = useState<SystemTrajectory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    reportingApi
      .systemTrajectory(Math.max(periodDays, 90), 5)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Failed to load trajectory'))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [periodDays]);

  if (loading && !data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  if (error || !data) {
    return <p className="text-sm text-muted-foreground py-8 text-center">{error || 'No trajectory data.'}</p>;
  }

  const v = VERDICT[data.verdict] || VERDICT.insufficient_data;
  const insufficient = data.verdict === 'insufficient_data';
  const o = data.overall;
  const f = data.forecast;
  const suff = data.data_sufficiency;

  return (
    <div className="space-y-4">
      {/* ── Verdict banner ─────────────────────────────────────────────── */}
      <Card className={`relative overflow-hidden border ${v.ring}`}>
        <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${v.glow}`} />
        <CardContent className="relative p-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              <div className={`rounded-xl border ${v.ring} bg-background/60 p-3 ${v.text}`}>
                <v.Icon className="w-7 h-7" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Autonomous system</p>
                  {!insufficient && (
                    <Badge variant="outline" className={v.chip}>
                      {Math.round(data.confidence * 100)}% confidence
                    </Badge>
                  )}
                </div>
                <h2 className={`font-display text-2xl font-bold ${v.text}`}>{v.label}</h2>
              </div>
            </div>
            {!insufficient && (
              <div className="grid grid-cols-3 gap-2 md:gap-3 text-center">
                <div>
                  <p className="text-[11px] text-muted-foreground">Total P&L</p>
                  <p className={`text-xl font-bold ${pnlClass(o.total_pnl)}`}>{fmtMoney(o.total_pnl)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Win rate</p>
                  <p className="text-xl font-bold">{o.win_rate.toFixed(0)}%</p>
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground">Trades</p>
                  <p className="text-xl font-bold">{o.total_trades}</p>
                </div>
              </div>
            )}
          </div>

          {/* NL insight narrative */}
          <div className="relative mt-4 flex gap-2 rounded-lg border bg-background/50 p-3">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-primary" />
            <p className="text-sm leading-relaxed text-foreground/90">{data.narrative}</p>
          </div>
        </CardContent>
      </Card>

      {insufficient ? (
        /* ── Warming-up state ────────────────────────────────────────── */
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Telescope className="w-4 h-4 text-primary" /> What activates this dashboard
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Closed trades" value={`${suff.closed_trades} / ${suff.min_trades_for_trend}`} />
              <KPI label="Market days traded" value={String(suff.trading_days)} />
            </div>
            <ul className="space-y-1.5 text-muted-foreground">
              <li className="flex items-center gap-2">
                {suff.forecast_ready ? <ArrowUpRight className="w-4 h-4 text-emerald-500" /> : <Minus className="w-4 h-4" />}
                Forecast unlocks at {suff.min_trades_for_forecast} closed trades
              </li>
              <li className="flex items-center gap-2">
                {suff.trend_ready ? <ArrowUpRight className="w-4 h-4 text-emerald-500" /> : <Minus className="w-4 h-4" />}
                Improving / degrading verdict unlocks at {suff.min_trades_for_trend} closed trades
              </li>
            </ul>
            <p className="text-xs text-muted-foreground border-t pt-3">
              The pipeline is live — closed trades now persist and flow here automatically. Track live
              scanning, pattern matches, and signals on the <span className="text-foreground font-medium">Fleet</span> page
              in the meantime.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* ── KPI strip ────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="Expectancy / trade" value={fmtMoney(o.expectancy, 2)} valueClass={pnlClass(o.expectancy)} />
            <KPI label="Profit factor" value={o.profit_factor.toFixed(2)} />
            <KPI label="Sharpe" value={(o.sharpe ?? 0).toFixed(2)} />
            <KPI label="Best trade" value={fmtMoney(o.best_trade ?? 0)} valueClass="text-emerald-500" />
            <KPI label="Worst trade" value={fmtMoney(o.worst_trade ?? 0)} valueClass="text-rose-500" />
          </div>

          {/* ── Trajectory + forecast (hero chart) ─────────────────────── */}
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" /> Trajectory &amp; forecast
                <span className="ml-auto flex items-center gap-2 text-xs font-normal">
                  <span className="text-muted-foreground">cumulative P&amp;L</span>
                  {f.ready && (
                    <Badge variant="outline" className={(f.projected_pnl ?? 0) >= 0 ? VERDICT.improving.chip : VERDICT.degrading.chip}>
                      {fmtMoney(f.projected_pnl ?? 0)} · {f.horizon_days ?? 5}d
                    </Badge>
                  )}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TrajectoryChart
                points={data.trajectory.map((p) => ({ date: p.date, cumulative_pnl: p.cumulative_pnl }))}
                projectedPnl={f.ready ? f.projected_cumulative_pnl : null}
                horizonDays={f.horizon_days ?? 5}
              />
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {/* drivers */}
                {data.drivers.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {data.drivers.map((d) => (
                      <span
                        key={d.metric}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${
                          d.favorable
                            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500'
                            : 'border-rose-500/30 bg-rose-500/10 text-rose-500'
                        }`}
                      >
                        {d.direction === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {d.metric.replace(/_/g, ' ')} {d.from}→{d.to}
                      </span>
                    ))}
                  </div>
                ) : <span />}
                {/* forecast detail */}
                {f.ready && (
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className="text-[11px] text-muted-foreground">Trend</p>
                      <p className={`text-sm font-semibold ${pnlClass(f.daily_slope ?? 0)}`}>{fmtMoney(f.daily_slope ?? 0)}/day</p>
                    </div>
                    <div className="w-24">
                      <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
                        <span>fit</span><span>{Math.round((f.confidence ?? 0) * 100)}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${(f.confidence ?? 0) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* ── Winning trades highlights ──────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" /> Winning trades
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.top_winners.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No winning trades in this window yet.</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {data.top_winners.map((w, i) => (
                    <div key={`${w.symbol}-${i}`} className="rounded-lg border bg-card/50 p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold">{w.symbol}</span>
                        <span className="text-emerald-500 font-bold">{fmtMoney(w.realized_pnl, 2)}</span>
                      </div>
                      <div className="flex items-center justify-between mt-1 text-xs text-muted-foreground">
                        <span className="truncate pr-2">{w.pattern_name || '—'}</span>
                        {w.return_pct != null && <span className="text-emerald-500">+{w.return_pct.toFixed(1)}%</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
